import { z } from 'zod';

export const LOCALES = ['en', 'sq'] as const;
export const DEFAULT_LOCALE = 'en' satisfies Locale;
export const localeSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof localeSchema>;
export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', sq: 'Shqip' };

/* ------------------------------------------------------------------ */
/* Field metadata — drives the admin's generated forms                 */
/* ------------------------------------------------------------------ */

/**
 * How the admin renders a field. Attached with `.meta({ widget })` and exported through
 * `z.toJSONSchema()`, so the admin form, the API validation and HTML sanitizing all come
 * from this one definition.
 */
export type Widget = 'text' | 'textarea' | 'html' | 'image' | 'file' | 'url' | 'email';

declare module 'zod' {
  interface GlobalMeta {
    widget?: Widget;
    /** For arrays of objects: the property used as each item's label in the editor. */
    itemLabel?: string;
  }
}

const trimmed = (max: number) => z.string().trim().max(max);
const required = (max: number) => trimmed(max).min(1, 'required');
/** Relative app path (/media/…, /uploads/…) or absolute http(s) URL. */
const urlOrPath = z
  .string()
  .trim()
  .max(2048)
  .refine((v) => v === '' || v.startsWith('/') || /^https?:\/\//.test(v), 'invalid url');

/** A full web address (http/https), or empty. For links that must open another website. */
const webUrl = z
  .string()
  .trim()
  .max(2048)
  .refine((v) => v === '' || (/^https?:\/\//i.test(v) && URL.canParse(v)), 'invalid url');

/** Field builders: a schema plus the label/widget the admin shows for it. */
export const f = {
  text: (title: string, max = 200, description?: string) =>
    required(max).meta({ title, description, widget: 'text' }),
  optionalText: (title: string, max = 200, description?: string) =>
    trimmed(max).default('').meta({ title, description, widget: 'text' }),
  lines: (title: string, max = 1000, description = 'Line breaks are kept.') =>
    required(max).meta({ title, description, widget: 'textarea' }),
  optionalLines: (title: string, max = 3000, description?: string) =>
    trimmed(max).default('').meta({ title, description, widget: 'textarea' }),
  richText: (title: string, description?: string) =>
    z.string().max(200_000).default('').meta({ title, description, widget: 'html' }),
  image: (title: string, description?: string) =>
    urlOrPath.min(1, 'required').meta({ title, description, widget: 'image' }),
  optionalImage: (title: string, description?: string) =>
    urlOrPath.default('').meta({ title, description, widget: 'image' }),
  file: (title: string, description?: string) =>
    urlOrPath.min(1, 'required').meta({ title, description, widget: 'file' }),
  url: (title: string, description?: string) =>
    urlOrPath.meta({ title, description, widget: 'url' }),
  email: (title: string) => z.email().max(254).meta({ title, widget: 'email' }),
  list: <T extends z.ZodType>(title: string, item: T, itemLabel?: string, description?: string) =>
    z.array(item).default([]).meta({ title, description, itemLabel }),
};

const honeypot = z.string().max(500).optional();

/* ------------------------------------------------------------------ */
/* Public forms                                                        */
/* ------------------------------------------------------------------ */

/**
 * How long the form was open before it was sent (ms), filled in by the website's forms. Used
 * only by the spam filter; missing or very short values mark the submission as possible spam.
 */
const formElapsed = z.coerce.number().int().min(0).max(1_000_000_000).optional();

export const contactMessageInput = z.object({
  name: required(120),
  email: z.email().max(254),
  phone: required(40).regex(/^[+()\d\s.-]{5,}$/, 'invalid phone'),
  message: required(5000),
  /** Honeypot: hidden from humans; the API accepts but silently discards filled-in values. */
  website: honeypot,
  elapsed: formElapsed,
});
export type ContactMessageInput = z.infer<typeof contactMessageInput>;

export const jobApplicationInput = z.object({
  position: required(160),
  phone: required(40).regex(/^[+()\d\s.-]{5,}$/, 'invalid phone'),
  website: honeypot,
  elapsed: formElapsed,
});
export type JobApplicationInput = z.infer<typeof jobApplicationInput>;

export const CV_MAX_BYTES = 3 * 1024 * 1024;
export const CV_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;
export const CV_EXTENSIONS = ['.pdf', '.doc', '.docx'] as const;

/**
 * Quick first check for a CV: the type the browser reports *or* the file name. Some devices
 * (Windows without Office, some Android phones) report no type or a generic one for Word files,
 * so the name must be enough. The server then checks the real content, which is what decides.
 */
export const looksLikeCv = (name: string, type: string) =>
  (CV_MIME_TYPES as readonly string[]).includes(type) ||
  CV_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

/* ------------------------------------------------------------------ */
/* Auth & users                                                        */
/* ------------------------------------------------------------------ */

export const ROLES = ['admin', 'editor'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

const password = z.string().min(10, 'at least 10 characters').max(200);

export const loginInput = z.object({
  email: z.email().max(254),
  password: z.string().min(8).max(200),
});
export type LoginInput = z.infer<typeof loginInput>;

export const userCreateInput = z.object({
  name: required(120),
  email: z.email().max(254),
  role: roleSchema,
  password,
});
export type UserCreateInput = z.infer<typeof userCreateInput>;

export const userUpdateInput = z.object({
  name: required(120),
  role: roleSchema,
  active: z.boolean(),
  /** Optional password reset by an admin (the user must change it at next sign-in). */
  password: password.optional().or(z.literal('')),
  /** Turn off the user's two-factor authentication (e.g. lost phone). */
  resetTwoFactor: z.boolean().optional(),
});
export type UserUpdateInput = z.infer<typeof userUpdateInput>;

export const passwordChangeInput = z
  .object({ currentPassword: z.string().min(1), newPassword: password })
  .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'The new password must be different from your current one',
    path: ['newPassword'],
  });
export type PasswordChangeInput = z.infer<typeof passwordChangeInput>;

export type AdminUserDto = {
  id: number;
  email: string;
  name: string;
  role: Role;
  mustChangePassword: boolean;
  twoFactorEnabled: boolean;
};
/** The signed-in user, plus what they must do before the admin opens up. */
export type SessionUserDto = AdminUserDto & {
  /** Admins must turn on two-step verification before using the admin (when required). */
  mustSetUpTwoFactor: boolean;
};
export type UserDto = AdminUserDto & {
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
};

/** Login answers with the user, or asks for the authenticator code first. */
export type LoginResponse = SessionUserDto | { twoFactorRequired: true; ticket: string };

const totpCode = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code');

export const twoFactorVerifyInput = z.object({
  ticket: z.string().min(10).max(2000),
  code: totpCode,
});
export const twoFactorEnableInput = z.object({
  /** Re-confirmed so someone at an unlocked, signed-in computer can't bind their own phone. */
  password: z.string().min(1).max(200),
  secret: z.string().regex(/^[A-Z2-7]{32}$/),
  code: totpCode,
});
export const twoFactorDisableInput = z.object({
  password: z.string().min(1).max(200),
  code: totpCode,
});
export type TwoFactorSetupDto = { secret: string; otpauthUrl: string; qrDataUrl: string };

/* ------------------------------------------------------------------ */
/* Optimistic concurrency                                              */
/* ------------------------------------------------------------------ */

/** `version` = the `updatedAt` the editor loaded; the API refuses to overwrite newer data. */
const version = z.iso.datetime({ offset: true }).optional();

export const reorderInput = z.object({ ids: z.array(z.number().int().positive()).min(1).max(500) });

/* ------------------------------------------------------------------ */
/* Events                                                              */
/* ------------------------------------------------------------------ */

export const EVENT_KINDS = ['ceo', 'other'] as const;
export const eventKindSchema = z.enum(EVENT_KINDS);
export type EventKind = z.infer<typeof eventKindSchema>;

export const eventTranslationInput = z.object({
  title: required(200),
  edition: trimmed(80).default(''),
  /** Free-form date / venue lines, separated by newlines. */
  details: trimmed(500).default(''),
  /** Optional wording for the home page calendar card (empty = same as above). */
  homeEdition: trimmed(80).default(''),
  homeDetails: trimmed(500).default(''),
});

export const eventInput = z.object({
  kind: eventKindSchema,
  year: z.coerce.number().int().min(1990).max(2100),
  logo: urlOrPath.min(1, 'required'),
  /** Events always link to a website (the fair's own site), never to a file or site page. */
  url: webUrl.default(''),
  sort: z.coerce.number().int().default(0),
  published: z.boolean().default(true),
  featured: z.boolean().default(false),
  translations: z.object({ en: eventTranslationInput, sq: eventTranslationInput }),
  version,
});
export type EventInput = z.infer<typeof eventInput>;

export type EventDto = {
  id: number;
  kind: EventKind;
  year: number;
  logo: string;
  url: string;
  sort: number;
  featured: boolean;
  title: string;
  edition: string;
  details: string;
  homeEdition: string;
  homeDetails: string;
};

/* ------------------------------------------------------------------ */
/* Services                                                            */
/* ------------------------------------------------------------------ */

/** A service gallery photo. `caption` is English; `captionSq` the Albanian one (falls back to English). */
export const galleryItem = z.object({
  image: urlOrPath,
  caption: trimmed(200).default(''),
  captionSq: trimmed(200).default(''),
});
export type GalleryItem = z.infer<typeof galleryItem>;
/** A gallery photo as the public site gets it: one caption, in the page's language. */
export type LocalizedGalleryItem = { image: string; caption: string };

export const serviceTranslationInput = z.object({
  /** Card title on the home page (and the default everywhere else). */
  title: required(200),
  /** Optional card title on the services overview page. */
  listTitle: trimmed(200).default(''),
  /** Optional heading on the service's own page. */
  heading: trimmed(200).default(''),
  /** One-line description under the title on the service cards. */
  summary: trimmed(300).default(''),
  /** Sanitized HTML body. */
  body: z.string().max(100_000).default(''),
});

export const serviceInput = z.object({
  slug: required(160).regex(/^[a-z0-9-]+$/, 'lowercase letters, numbers and dashes only'),
  /** Icon on dark backgrounds (home page) */
  icon: urlOrPath.min(1, 'required'),
  /** Icon on light backgrounds (services pages) */
  iconLight: urlOrPath.min(1, 'required'),
  sort: z.coerce.number().int().default(0),
  gallery: z.array(galleryItem).default([]),
  translations: z.object({ en: serviceTranslationInput, sq: serviceTranslationInput }),
  version,
});
export type ServiceInput = z.infer<typeof serviceInput>;

export type ServiceDto = {
  id: number;
  slug: string;
  icon: string;
  iconLight: string;
  sort: number;
  title: string;
  listTitle: string;
  heading: string;
  summary: string;
  body: string;
  gallery: LocalizedGalleryItem[];
};

/* ------------------------------------------------------------------ */
/* Photo gallery (home page)                                           */
/* ------------------------------------------------------------------ */

export const galleryCategoryInput = z.object({
  title: z.object({ en: required(120), sq: required(120) }),
  published: z.boolean().default(true),
});
export type GalleryCategoryInput = z.infer<typeof galleryCategoryInput>;

/** Photo formats kept as uploaded (full quality). GIF/SVG aren't photos. */
export const GALLERY_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
/** Originals are kept at full quality, so they may be larger than other uploads. */
export const GALLERY_MAX_BYTES = 30 * 1024 * 1024;

export type GalleryPhotoDto = {
  id: number;
  /** The original, full-quality file (shown in the full-screen viewer). */
  url: string;
  /** Small WebP copy for the grid. */
  thumbUrl: string;
  width: number;
  height: number;
};

/** Public: a published category with its photos, titled in the requested language. */
export type GalleryCategoryDto = { id: number; title: string; photos: GalleryPhotoDto[] };

export type GalleryCategoryAdminDto = {
  id: number;
  title: Record<Locale, string>;
  published: boolean;
  sort: number;
  updatedAt: string;
  photos: (GalleryPhotoDto & { size: number; source: 'upload' | 'bundled' })[];
};

/* ------------------------------------------------------------------ */
/* Videos (home page)                                                  */
/* ------------------------------------------------------------------ */

/**
 * The 11-character ID of a YouTube video, from a link (watch, youtu.be, shorts, embed, live)
 * or the bare ID; `null` if it isn't one.
 */
export function youtubeId(input: string): string | null {
  const v = input.trim();
  if (/^[\w-]{11}$/.test(v)) return v;
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^(www|m|music)\./, '');
  const id =
    host === 'youtu.be'
      ? url.pathname.slice(1)
      : host === 'youtube.com' || host === 'youtube-nocookie.com'
        ? url.pathname === '/watch'
          ? url.searchParams.get('v')
          : url.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/]+)/)?.[1]
        : null;
  return id && /^[\w-]{11}$/.test(id) ? id : null;
}

export const videoCategoryInput = galleryCategoryInput;
export type VideoCategoryInput = GalleryCategoryInput;

export const videoInput = z.object({
  /** A YouTube link (or ID); stored as the video ID. */
  youtubeId: z
    .string()
    .trim()
    .transform((v, ctx) => {
      const id = youtubeId(v);
      if (!id) ctx.addIssue({ code: 'custom', message: 'Paste a YouTube video link' });
      return id ?? '';
    }),
  /** Albanian falls back to English when empty. */
  title: z.object({ en: required(200), sq: trimmed(200).default('') }),
});
export type VideoInput = z.infer<typeof videoInput>;

/** Public: a video titled in the requested language. */
export type VideoDto = { id: number; youtubeId: string; title: string };

/** Public: a published category with its videos, titled in the requested language. */
export type VideoCategoryDto = { id: number; title: string; videos: VideoDto[] };

export type VideoCategoryAdminDto = {
  id: number;
  title: Record<Locale, string>;
  published: boolean;
  sort: number;
  updatedAt: string;
  videos: { id: number; youtubeId: string; title: Record<Locale, string> }[];
};

/* ------------------------------------------------------------------ */
/* Stats                                                               */
/* ------------------------------------------------------------------ */

export const statInput = z.object({
  value: z.coerce.number().int().min(0).max(10_000_000),
  labels: z.object({ en: required(80), sq: required(80) }),
});
export type StatInput = z.infer<typeof statInput>;

export type StatDto = { id: number; value: number; label: string; sort: number };
export type StatAdminDto = {
  id: number;
  value: number;
  sort: number;
  labels: Record<Locale, string>;
};

/* ------------------------------------------------------------------ */
/* Site settings (per locale)                                          */
/* ------------------------------------------------------------------ */

export const SOCIAL_NETWORKS = [
  'facebook',
  'instagram',
  'linkedin',
  'youtube',
  'x',
  'tiktok',
] as const;

export const siteSettingsSchema = z.object({
  companyName: f.text('Company name', 200),
  address: f.lines('Address (contact page)', 300),
  footerAddress: f.lines('Address (footer)', 300),
  phones: f.list('Phone numbers', f.text('Phone number', 40)),
  email: f.email('Public e-mail'),
  mapEmbedUrl: f.url(
    'Google Maps embed URL',
    'Google Maps → Share → Embed a map → copy the src="…" value.',
  ),
  socials: f.list(
    'Social media',
    z.object({
      network: z.enum(SOCIAL_NETWORKS).meta({ title: 'Network' }),
      url: f.url('Profile URL'),
    }),
    'network',
  ),
  copyright: f.text('Copyright line', 200, 'Shown at the bottom of every page.'),
  metaDescription: f.optionalLines(
    'Default SEO description',
    300,
    'Used by search engines when a page has no description of its own.',
  ),
});
export type SiteSettings = z.infer<typeof siteSettingsSchema>;
export type SiteSettingsDto = { locale: Locale; settings: SiteSettings; updatedAt: string };

export const settingsUpdateInput = z.object({ settings: z.unknown(), version });

/* ------------------------------------------------------------------ */
/* Pages — each slug has its own strongly typed, self-describing shape */
/* ------------------------------------------------------------------ */

const hero = {
  heroTitle: f.optionalLines(
    'Banner title',
    300,
    'Large text on the banner. Line breaks are kept.',
  ),
  heroImage: f.optionalImage('Banner image', 'Wide image, at least 1680 × 500 px.'),
};

export const pageSchemas = {
  home: z.object({
    heroTitle: hero.heroTitle,
    heroImage: hero.heroImage,
    servicesTitle: f.text('"Services" section title', 120),
    eventsTitle: f.text('"Events calendar" section title', 120),
    eventsCta: f.text('"See full calendar" button', 80),
    taglineLead: f.text('Tagline (first line)', 300),
    taglineHighlight: f.text('Tagline (highlighted line)', 300),
  }),
  'company-background': z.object({
    ...hero,
    title: f.text('Title', 200),
    intro: f.richText('Introduction'),
    missionTitle: f.text('Mission section title', 200),
    mission: f.richText('Mission & vision text'),
    factsTitle: f.text('Timeline title', 200),
    factsHint: f.optionalText('Timeline hint', 200),
    timeline: f.list(
      'Timeline',
      z.object({
        year: f.text('Year', 20),
        title: f.optionalText('Title', 300),
        text: f.optionalLines('Text', 3000),
        images: f.list('Badges / logos', f.image('Image')),
      }),
      'year',
    ),
    galleryTitle: f.text('Gallery title', 120),
    gallery: f.list(
      'Gallery links',
      z.object({
        title: f.text('Title', 120),
        image: f.image('Icon'),
        url: f.url('Link (page, video channel or PDF); empty opens the photo gallery'),
      }),
      'title',
    ),
  }),
  'our-clients': z.object({
    ...hero,
    title: f.text('Title', 200),
    intro: f.richText('Introduction'),
    groups: f.list(
      'Client groups',
      z.object({
        title: f.text('Group name', 200),
        items: f.list('Clients', f.text('Client', 300)),
      }),
      'title',
    ),
  }),
  'our-staff': z.object({
    ...hero,
    title: f.text('Title', 200),
    intro: f.richText('Introduction'),
    careerTitle: f.text('Career section title', 200),
    positionsTitle: f.text('"Open positions" title', 200),
    positions: f.list(
      'Open positions',
      z.object({ title: f.text('Position', 200), description: f.richText('Description') }),
      'title',
      'Each position also appears in the application form.',
    ),
    cvTitle: f.text('Application form title', 200),
    applyLabel: f.text('"Apply for" label', 200),
    phonePlaceholder: f.text('Phone field placeholder', 80),
    fileHint: f.optionalLines('File hint', 300),
    submitLabel: f.text('Submit button', 60),
  }),
  services: z.object({ ...hero, title: f.text('Title', 200), intro: f.richText('Introduction') }),
  events: z.object({ ...hero, title: f.text('Title', 200), ctaLabel: f.text('Banner button', 60) }),
  'other-events': z.object({
    ...hero,
    title: f.text('Title', 200),
    ctaLabel: f.text('Banner button', 60),
  }),
  'contact-us': z.object({
    ...hero,
    title: f.text('Title', 200),
    formTitle: f.text('Form title', 120),
    resourcesTitle: f.text('Downloads title', 120),
    resources: f.list(
      'Downloads',
      z.object({ label: f.text('Label', 120), url: f.file('File') }),
      'label',
      'Company name, address, phones, e-mail and map come from Settings.',
    ),
  }),
  'data-protection': z.object({ ...hero, title: f.text('Title', 200), body: f.richText('Text') }),
} as const;

export type PageSlug = keyof typeof pageSchemas;
export const PAGE_SLUGS = Object.keys(pageSchemas) as PageSlug[];
export const pageSlugSchema = z.enum(PAGE_SLUGS as [PageSlug, ...PageSlug[]]);
export type PageContent<S extends PageSlug> = z.infer<(typeof pageSchemas)[S]>;

export const PAGE_TITLES: Record<PageSlug, string> = {
  home: 'Home',
  'company-background': 'Company background',
  'our-clients': 'Our clients',
  'our-staff': 'Our staff & careers',
  services: 'Services (overview)',
  events: 'Our events',
  'other-events': 'Other events',
  'contact-us': 'Contact',
  'data-protection': 'Data protection',
};

/** Public path of each page (without locale prefix). */
export const PAGE_PATHS: Record<PageSlug, string> = {
  home: '/',
  'company-background': '/company-background',
  'our-clients': '/our-clients',
  'our-staff': '/our-staff',
  services: '/services',
  events: '/events',
  'other-events': '/other-events',
  'contact-us': '/contact-us',
  'data-protection': '/data-protection',
};

export type PageDto<S extends PageSlug = PageSlug> = {
  slug: S;
  locale: Locale;
  metaTitle: string;
  metaDescription: string;
  content: PageContent<S>;
  updatedAt: string;
};

export const pageUpdateInput = z.object({
  metaTitle: required(200),
  metaDescription: trimmed(300).default(''),
  content: z.unknown(),
  version,
});

/** Paths (e.g. `intro`, `positions[].description`) of rich-text fields in a schema. */
export function htmlFieldPaths(schema: z.ZodType): string[] {
  const out: string[] = [];
  const walk = (node: Record<string, unknown>, path: string) => {
    if (node.widget === 'html') out.push(path);
    const props = node.properties as Record<string, Record<string, unknown>> | undefined;
    for (const [k, v] of Object.entries(props ?? {})) walk(v, path ? `${path}.${k}` : k);
    const items = node.items as Record<string, unknown> | undefined;
    if (items) walk(items, `${path}[]`);
  };
  walk(
    z.toJSONSchema(schema, { io: 'input', unrepresentable: 'any' }) as Record<string, unknown>,
    '',
  );
  return out;
}

/* ------------------------------------------------------------------ */
/* Media library                                                       */
/* ------------------------------------------------------------------ */

export const MEDIA_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/avif',
] as const;
export const MEDIA_DOCUMENT_TYPES = ['application/pdf'] as const;
export const MEDIA_MAX_BYTES = 20 * 1024 * 1024;

export type MediaDto = {
  id: number;
  url: string;
  thumbUrl: string | null;
  fileName: string;
  mimeType: string;
  size: number;
  width: number | null;
  height: number | null;
  alt: string;
  source: 'upload' | 'bundled';
  createdAt: string;
};

export const mediaUpdateInput = z.object({ alt: trimmed(300) });

export type MediaUsage = {
  type: 'page' | 'service' | 'event' | 'settings';
  label: string;
  href: string;
};

/* ------------------------------------------------------------------ */
/* Admin                                                               */
/* ------------------------------------------------------------------ */

export type ContactMessageDto = {
  id: number;
  name: string;
  email: string;
  phone: string;
  message: string;
  read: boolean;
  /** Why the spam filter flagged it; null when it looks genuine. */
  spamReason: string | null;
  createdAt: string;
};

export type JobApplicationDto = {
  id: number;
  position: string;
  phone: string;
  fileName: string;
  read: boolean;
  /** Why the spam filter flagged it; null when it looks genuine. */
  spamReason: string | null;
  createdAt: string;
};

export const AUDIT_ACTIONS = ['create', 'update', 'delete', 'reorder', 'login', 'upload'] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export type ActivityDto = {
  id: number;
  action: AuditAction;
  entity: string;
  entityId: string | null;
  summary: string;
  user: { id: number; name: string } | null;
  createdAt: string;
};

export type ApiError = { error: { code: string; message: string; details?: unknown } };
